import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { WorkspaceGroup, WorkspaceOrganizationListItem } from '@immo/api-contract';
import { cn, Modal } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';
import { writeNarrowedOrganizationId, writeSelectedOrganizationId } from '@/lib/selected-organization';
import { useNarrowStandort } from './use-narrow-standort';
import { invalidateWorkspaceContext } from '@/lib/auth/workspace-context';
import { workspacePath } from '@/lib/workspace-path';
import { CheckIcon, ChevronDownIcon } from './icons';

type Props = {
  readonly organizations: ReadonlyArray<WorkspaceOrganizationListItem>;
  // The Standort the lists are narrowed to, or `null` for the default aggregate.
  readonly narrowedOrganizationId: string | null;
  // True for Owner / Workspace-Admin — drives the aggregate label.
  readonly seesAllStandorte: boolean;
  readonly collapsed: boolean;
  readonly className?: string;
  // Workspace-grouped affiliations from `/auth/me`. When the member belongs to
  // more than one workspace, the modal groups Standorte by workspace and lets
  // them switch the active workspace. Omitted (single-workspace) keeps the flat
  // layout.
  readonly workspaces?: ReadonlyArray<WorkspaceGroup>;
  // The workspace currently in context — its group renders the permission-aware
  // `organizations` list; other workspaces render their affiliated Standorte.
  readonly activeWorkspaceId?: string;
  // Workspaces the member cannot enter without dead-ending on the company-blocked
  // screen (not-onboarded + no permission to onboard, IMM-322). Rendered disabled
  // with an explanatory tooltip so the member can't strand themselves.
  readonly blockedWorkspaceIds?: ReadonlySet<string>;
};

// A single selectable row in the modal — the aggregate, a Standort, or a
// foreign-workspace Standort. `selected` shows the check for the active scope.
// `depth` indents the row so a Standort's place in the org tree is visible; a
// guide line on nested rows reinforces the parent/child relationship.
// `context` renders an ancestor the member can't act on (see `list-my-
// organizations`): dimmed and non-interactive, present only to root the tree.
function OptionRow({
  label,
  selected,
  depth = 0,
  context = false,
  disabled = false,
  title,
  onClick,
}: {
  readonly label: string;
  readonly selected?: boolean;
  readonly depth?: number;
  readonly context?: boolean;
  // Non-interactive because switching here would dead-end the member (a
  // not-onboarded workspace they cannot onboard). `title` is the explanatory
  // tooltip. Distinct from `context`, which only roots the org tree.
  readonly disabled?: boolean;
  readonly title?: string | undefined;
  readonly onClick?: (() => void) | undefined;
}) {
  if (context) {
    return (
      <div className="flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-left text-base text-text-tertiary">
        <span className="flex min-w-0 flex-1 items-center gap-2" style={{ paddingLeft: depth * 16 }}>
          {depth > 0 && <span aria-hidden className="h-4 w-px shrink-0 bg-border" />}
          <span className="min-w-0 flex-1 truncate">{label}</span>
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-disabled={disabled || undefined}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-left text-base transition-colors duration-micro ease-out',
        disabled
          ? 'cursor-not-allowed text-text-tertiary'
          : 'text-text-primary hover:bg-hover-bg focus-visible:outline-none focus-visible:bg-hover-bg focus-visible:shadow-focus-row',
      )}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2" style={{ paddingLeft: depth * 16 }}>
        {depth > 0 && <span aria-hidden className="h-4 w-px shrink-0 bg-border" />}
        <span className="min-w-0 flex-1 truncate">{label}</span>
      </span>
      {selected === true && <CheckIcon className="h-4 w-4 shrink-0 text-interactive" aria-hidden />}
    </button>
  );
}

// Order a flat Standort list depth-first (parents before their children) and tag
// each with its tree depth, so the modal can indent nested Standorte. Orgs whose
// parent is not in the list (e.g. a narrowed subtree) are treated as roots, so
// nothing is dropped.
function orderByHierarchy<T extends { id: string; parentId: string | null }>(
  orgs: ReadonlyArray<T>,
): Array<{
  org: T;
  depth: number;
}> {
  const present = new Set(orgs.map((o) => o.id));
  const childrenByParent = new Map<string | null, T[]>();

  for (const org of orgs) {
    const key = org.parentId !== null && present.has(org.parentId) ? org.parentId : null;
    const siblings = childrenByParent.get(key) ?? [];
    siblings.push(org);
    childrenByParent.set(key, siblings);
  }

  const ordered: Array<{ org: T; depth: number }> = [];
  const visit = (parentId: string | null, depth: number) => {
    for (const org of childrenByParent.get(parentId) ?? []) {
      ordered.push({ org, depth });
      visit(org.id, depth + 1);
    }
  };

  visit(null, 0);

  return ordered;
}

function GroupHeader({ children }: { readonly children: string }) {
  return (
    <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-text-tertiary">{children}</div>
  );
}

export function LocationSelector({
  organizations,
  narrowedOrganizationId,
  seesAllStandorte,
  collapsed,
  className,
  workspaces = [],
  activeWorkspaceId,
  blockedWorkspaceIds,
}: Props) {
  const t = useTranslations('pages.shell.location-selector');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const narrowStandort = useNarrowStandort();

  // Hide the selector only when there is nothing to switch between: a single
  // workspace with just the Hauptstandort. A member in several workspaces always
  // gets the switcher, even if each has only one Standort.
  // Ancestor context rows (`accessible === false`) aren't switchable Standorte,
  // so they don't on their own warrant showing the selector.
  const selectableStandorte = organizations.filter((org) => org.accessible !== false);
  const hasMultipleStandorte = selectableStandorte.length > 1;
  const hasMultipleWorkspaces = workspaces.length > 1;

  if (!hasMultipleStandorte && !hasMultipleWorkspaces) return null;

  const narrowed =
    narrowedOrganizationId === null ? null : (organizations.find((o) => o.id === narrowedOrganizationId) ?? null);

  const aggregateLabel = seesAllStandorte ? t('all-standorte') : t('all-my-standorte');
  const activeWorkspaceName = hasMultipleWorkspaces
    ? (workspaces.find((w) => w.id === activeWorkspaceId)?.name ?? null)
    : null;
  // The aggregate item scopes to the active workspace, so name it accordingly
  // when the member spans several workspaces — "all my locations" would read as
  // cross-workspace, which we never do.
  const activeAggregateLabel =
    activeWorkspaceName === null ? aggregateLabel : t('all-workspace-standorte', { workspace: activeWorkspaceName });
  const initial = (narrowed?.name ?? aggregateLabel).charAt(0).toUpperCase();

  // Narrow within the active workspace.
  function select(orgId: string | null) {
    setOpen(false);

    if (orgId === narrowedOrganizationId) return;

    narrowStandort(orgId);
  }

  // Switch to a Standort in a DIFFERENT workspace: navigate to that workspace's
  // URL (`/w/:id`) — the workspace now lives in the path, so the route change IS
  // the switch. Narrow AND select the chosen Standort first so the re-resolved
  // context scopes both the visible lists and the member's permissions to it.
  // Selecting (not just narrowing) matters: permissions are resolved for the
  // SELECTED org, so a member who is only org-scoped in this foreign workspace is
  // correctly limited to that Standort instead of falling back to its first org.
  function switchWorkspace(workspaceSlug: string, orgId: string) {
    setOpen(false);
    writeSelectedOrganizationId(orgId);
    writeNarrowedOrganizationId(orgId);
    invalidateWorkspaceContext();
    navigate(workspacePath(workspaceSlug));
  }

  // Switch to a DIFFERENT workspace's aggregate ("all locations") view: navigate
  // to that workspace's URL with the narrow cleared so every Standort the member
  // can see in it shows. We still select a concrete org so permissions resolve
  // against the new workspace; the first affiliated Standort is a safe base.
  function switchWorkspaceAggregate(workspaceSlug: string, baseOrgId: string) {
    setOpen(false);
    writeSelectedOrganizationId(baseOrgId);
    writeNarrowedOrganizationId(null);
    invalidateWorkspaceContext();
    navigate(workspacePath(workspaceSlug));
  }

  // Other workspaces the member can switch into (the active one renders its full
  // permission-aware Standort list instead of the affiliation summary).
  const otherWorkspaces = hasMultipleWorkspaces
    ? workspaces.filter((w) => w.id !== activeWorkspaceId && w.organisations.length > 0)
    : [];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={collapsed ? t('modal-title') : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          'flex h-topbar w-full items-center border-b border-border',
          collapsed ? 'justify-center px-0' : 'gap-2 px-4',
          'cursor-pointer transition-colors duration-micro ease-out hover:bg-hover-bg focus-visible:shadow-focus focus-visible:outline-none',
          className,
        )}
      >
        <span
          aria-hidden
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-interactive text-base font-semibold text-interactive-on"
        >
          {initial}
        </span>
        {!collapsed && (
          <>
            <span className="flex min-w-0 flex-1 flex-col text-left leading-tight">
              <span className="truncate text-base font-semibold tracking-tight text-text-primary">
                {narrowed === null ? activeAggregateLabel : narrowed.name}
              </span>
              {activeWorkspaceName !== null && (
                <span className="truncate text-xs font-medium uppercase tracking-wide text-text-tertiary">
                  {activeWorkspaceName}
                </span>
              )}
            </span>
            <ChevronDownIcon className="h-4 w-4 shrink-0 text-text-secondary" aria-hidden />
          </>
        )}
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="sm"
        title={t('modal-title')}
        confirmOnClose={false}
        dismissOnBackdrop
        className="rounded-2xl"
      >
        <div className="-my-2 flex flex-col">
          <GroupHeader>{activeWorkspaceName ?? t('standort-label')}</GroupHeader>
          <OptionRow
            label={activeAggregateLabel}
            selected={narrowedOrganizationId === null}
            onClick={() => select(null)}
          />
          {orderByHierarchy(organizations).map(({ org, depth }) => {
            // Ancestor rows (`accessible === false`) only root the tree — they
            // are not affiliations the member can narrow to.
            const context = org.accessible === false;

            return (
              <OptionRow
                key={org.id}
                label={org.name}
                depth={depth}
                context={context}
                selected={org.id === narrowedOrganizationId}
                onClick={context ? undefined : () => select(org.id)}
              />
            );
          })}
          {otherWorkspaces.map((ws) => {
            // `otherWorkspaces` only keeps workspaces with at least one Standort,
            // so the first one is a safe permission base for the aggregate switch.
            const baseOrg = ws.organisations[0];
            // A workspace whose company onboarding the member cannot complete is a
            // dead-end: switching in would strand them on the blocking screen.
            const blocked = blockedWorkspaceIds?.has(ws.id) === true;
            const blockedTitle = blocked ? t('not-onboarded') : undefined;

            return (
              <div key={ws.id} className="mt-1 flex flex-col border-t border-border pt-1">
                <GroupHeader>{ws.name}</GroupHeader>
                {baseOrg !== undefined && (
                  <OptionRow
                    label={t('all-workspace-standorte', { workspace: ws.name })}
                    disabled={blocked}
                    title={blockedTitle}
                    onClick={blocked ? undefined : () => switchWorkspaceAggregate(ws.slug, baseOrg.id)}
                  />
                )}
                {ws.organisations.map((org) => (
                  <OptionRow
                    key={org.id}
                    label={org.name}
                    disabled={blocked}
                    title={blockedTitle}
                    onClick={blocked ? undefined : () => switchWorkspace(ws.slug, org.id)}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </Modal>
    </>
  );
}
